import { goto } from '$app/navigation';
import { AOFL_PRODUCT_NAMES, PAGES, QUERY_PARAM_KEYS } from '$lib/utils/constants';
import { LOGIN_ERROR_TYPES } from '../../../routes/login/__config/common';
import { redirect } from '@sveltejs/kit';
import { base } from '$app/paths';
import { z } from 'zod';
import { Sentry } from '$lib/services/sentry';
import { AnalyticsService } from '$lib/services/analytics';
import { user } from '$lib/stores/user/store';
import { app } from '$lib/stores/app/store';
import { get } from 'svelte/store';
import { has2_0Account } from '$lib/utils/aofl-accounts.js';
import { logout } from '$lib/stores/user/action.js';

// TODO: move types to a separate file.

/**
 * A loader function that returns a promise resolving to a module.
 * @typedef {Function} ModuleLoaderFunction
 * @returns {Promise<*>} Promise resolving to the imported module.
 */

/**
 * The result of import.meta.glob which maps each module file path to a loader function.
 * @typedef {{[key: string]: ModuleLoaderFunction}} GlobResult
 */

/**
 * @typedef {object} PolicyObject
 * @property {() => Promise<boolean>} policy - Function that returns a boolean indicating if access is allowed
 * @property {() => Promise<string>} [policyExceptionHandler] - Function that returns redirect path when policy fails
 */

/**
 * @typedef {object} AuthorizerProps
 * @property {string} routePath
 * @property {PolicyObject} policies
 */

const SchemaPolicyObject = z.object({
	policy: z.function().returns(z.promise(z.boolean())),
	policyExceptionHandler: z.function().returns(z.promise(z.string())).optional(),
});

const SchemaAuthorizerProps = z.object({
	policies: SchemaPolicyObject,
	routePath: z.string(),
});

const VISITED_PATHS_KEY = 'authorizer_visited_paths';

/**
 * Gets visited paths from sessionStorage.
 * @returns {Set<string>}
 */
function getVisitedPaths() {
	if (typeof window === 'undefined') return new Set();
	try {
		const raw = sessionStorage.getItem(VISITED_PATHS_KEY);
		return new Set(raw ? JSON.parse(raw) : []);
	} catch {
		return new Set();
	}
}

/**
 * Marks a path as visited. Returns true if path was already visited (cycle detected).
 * @param {string} path The current path
 * @returns {boolean}
 */
function markPathVisited(path) {
	if (typeof window === 'undefined') return false;
	try {
		const visited = getVisitedPaths();
		const hasCycle = visited.has(path);
		visited.add(path);
		sessionStorage.setItem(VISITED_PATHS_KEY, JSON.stringify([...visited]));
		return hasCycle;
	} catch {
		return false;
	}
}

/**
 * Clears visited paths.
 */
export function clearVisitedPaths() {
	if (typeof window === 'undefined') return;
	try {
		sessionStorage.removeItem(VISITED_PATHS_KEY);
	} catch {
		// Ignore storage errors
	}
}

/**
 * Logs redirect loop to Sentry.
 * @param {boolean} hasCycle - Whether a cycle was detected
 * @param {string} currentPath - Current route path
 * @param {string|null} pathToRedirect - Intended redirect path
 */
function logRedirectLoop(hasCycle, currentPath, pathToRedirect) {
	const currentUser = get(user);
	const productName = has2_0Account()
		? AOFL_PRODUCT_NAMES.ABCMOUSE_2_0
		: AOFL_PRODUCT_NAMES.ABCMOUSE;

	Sentry.captureException(new Error('Redirect cycle detected'), {
		tags: {
			issue: LOGIN_ERROR_TYPES.REDIRECT_LOOP,
			subscription_path: currentUser?.userMetaData?.subscription_path,
			completed_step: currentUser?.userMetaData?.completed_step,
			productName,
		},
		extra: {
			hasCycle,
			pathToRedirect,
			currentPath,
			visitedPaths: [...getVisitedPaths()],
		},
	});
}

export default class Authorizer {
	/**
	 * Creates an instance of Authorizer.
	 * @param {AuthorizerProps} props - properties
	 * @memberof Authorizer
	 */
	constructor(props) {
		this.propsSchema = SchemaAuthorizerProps;
		this.validate(props);
		this.policies = props.policies;
		this.routePath = props.routePath;
	}

	/**
	 *
	 *
	 * @param {AuthorizerProps} props - properties
	 * @returns {boolean}
	 * @memberof Authorizer
	 */
	validate(props) {
		const validation = this.propsSchema.safeParse(props);
		if (!validation.success) {
			if (import.meta.env.DEV) {
				console.error(`Invalid input: ${validation.error.message}`);
			} else {
				Sentry.addBreadcrumb({
					category: 'authorizer-validate',
					message: `Invalid input: ${validation.error.message}`,
					level: 'info',
				});
			}
			return false;
		}
		return true;
	}

	/**
	 * Authorizes the current route based on policies.
	 * @memberof Authorizer
	 * @returns {Promise<boolean>}
	 */
	async browserAuthorize() {
		const isActionAllow = await this.policies.policy();

		if (isActionAllow) {
			clearVisitedPaths();
			return true;
		}

		const currentPath = base ? base + this.routePath : this.routePath;

		let pathToRedirect = this.policies.policyExceptionHandler
			? await this.policies.policyExceptionHandler()
			: await Authorizer.defaultPolicyAuthorizer();

		const hasCycle = markPathVisited(currentPath);

		if (hasCycle) {
			logRedirectLoop(hasCycle, currentPath, pathToRedirect);
			clearVisitedPaths();

			await logout(get(app), { redirect: false });

			pathToRedirect =
				PAGES.INTERNAL.LOGIN + `?${QUERY_PARAM_KEYS.ERROR_TYPE}=${LOGIN_ERROR_TYPES.REDIRECT_LOOP}`;
		} else if (!pathToRedirect || currentPath === pathToRedirect) {
			logRedirectLoop(hasCycle, currentPath, pathToRedirect);

			pathToRedirect = await Authorizer.defaultPolicyAuthorizer();
		}

		Sentry.addBreadcrumb({
			category: 'navigation',
			message: `Authorizer redirecting to ${pathToRedirect}`,
			level: 'info',
		});
		await Authorizer.redirect(pathToRedirect);

		return false;
	}

	static async defaultPolicyAuthorizer() {
		AnalyticsService.trackClientDebug({
			eventSubType: 'default-policy-authorizer',
			source: 'Authorizer',
		});
		// TODO: create a functionality to force a transition without a policy check.
		return PAGES.INTERNAL.HOME;
	}

	/**
	 *
	 * @static
	 * @param {string} url - target url of the destination
	 * @returns {Promise<void>}
	 * @memberof Authorizer
	 */
	static async redirect(url) {
		if (typeof window === 'undefined') {
			throw redirect(301, url);
		}
		if (url.startsWith('http')) {
			return new Promise((resolve) => {
				window.location.href = url;
				resolve();
			});
		} else {
			return await goto(url);
		}
	}
}
